'use client'

import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  type ColumnDef,
  type ColumnOrderState,
  type Row,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2 } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import {
  DataTableContext,
  type DataTableContextValue,
  type LoadStrategy,
  useDataTableContext,
} from './context'
import {
  getAutoGridTemplate,
  getGridTemplate,
  useIntersectionObserver,
} from './hooks'

// ============================================================================
// Types
// ============================================================================

interface DataTableRootProps<TData> {
  /** Data items to display */
  data: TData[]
  /** Total count from API */
  totalCount: number
  /** Column definitions */
  columns: ColumnDef<TData, unknown>[]
  /** Loading state */
  isLoading?: boolean
  /** Fetching next page state */
  isFetchingNextPage?: boolean
  /** Whether more pages available */
  hasNextPage?: boolean
  /** Function to fetch next page */
  fetchNextPage?: () => void
  /** Function to fetch all remaining pages */
  fetchAllPages?: () => Promise<void>
  /** Whether all data has been loaded */
  isFullyLoaded?: boolean
  /** Estimated row height for virtualization (default: 48) */
  estimatedRowHeight?: number
  /** Default sorting state (won't trigger fetchAllPages on mount) */
  defaultSorting?: SortingState
  /** Column order (array of column IDs) - controlled state */
  columnOrder?: ColumnOrderState
  /** Column visibility (map of column ID to boolean) - controlled state */
  columnVisibility?: VisibilityState
  /** Children components */
  children: React.ReactNode
  /** Additional className */
  className?: string
}

interface DataTableHeaderProps {
  /** Additional className */
  className?: string
  /** Use auto-sizing for columns based on content (default: false) */
  autoSize?: boolean
  /** Minimum width for last column when using autoSize (e.g., '250px') */
  lastColumnMin?: string
  /** Make header sticky (use when inside ScrollArea) */
  sticky?: boolean
}

interface DataTableVirtualBodyProps<TData> {
  /** Render function for each row */
  renderRow: (row: Row<TData>, index: number) => React.ReactNode
  /** Maximum height for scrollable area (default: 500px). Ignored when inside ScrollArea. */
  maxHeight?: number | string
  /** Overscan count for virtualization (default: 5) */
  overscan?: number
  /** Additional className */
  className?: string
  /** When true, doesn't create its own scroll container (use inside ScrollArea) */
  useParentScroll?: boolean
}

interface DataTableLoadTriggerProps {
  /** Additional className */
  className?: string
}

interface DataTableEmptyProps {
  /** Empty message */
  children?: React.ReactNode
  /** Additional className */
  className?: string
}

interface DataTableScrollAreaProps {
  /** Maximum height for scrollable area */
  maxHeight: number | string
  /** Children (Header + VirtualBody) */
  children: React.ReactNode
  /** Additional className */
  className?: string
}

interface DataTableSkeletonProps {
  /** Number of skeleton rows (default: 5) */
  rowCount?: number
  /** Number of columns (default: 4) */
  columnCount?: number
  /** Additional className */
  className?: string
}

// ============================================================================
// Root Component
// ============================================================================

function DataTableRoot<TData>({
  data,
  totalCount,
  columns,
  isLoading = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  fetchNextPage = () => {},
  fetchAllPages = async () => {},
  isFullyLoaded = false,
  estimatedRowHeight = 48,
  defaultSorting = [],
  columnOrder,
  columnVisibility,
  children,
  className,
}: DataTableRootProps<TData>) {
  const tableId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const [sorting, setSorting] = useState<SortingState>(defaultSorting)
  const [loadStrategy, setLoadStrategy] = useState<LoadStrategy>('lazy')

  // Track if sort was user-initiated (not default/initial)
  const isUserInitiatedSort = useRef(false)

  // Create TanStack Table instance
  // Always enable sorting - we'll fetch all data when user clicks a sort header
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnOrder: columnOrder ?? [],
      columnVisibility: columnVisibility ?? {},
    },
    onSortingChange: (updater) => {
      // Mark as user-initiated when sort changes via interaction
      isUserInitiatedSort.current = true
      setSorting(updater)
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
  })

  // When sorting changes via user interaction and we don't have all data, fetch everything
  useEffect(() => {
    if (
      isUserInitiatedSort.current &&
      sorting.length > 0 &&
      !isFullyLoaded &&
      hasNextPage
    ) {
      setLoadStrategy('eager')
      fetchAllPages()
    }
  }, [sorting, isFullyLoaded, hasNextPage, fetchAllPages])

  // Reset to lazy strategy when fully loaded
  useEffect(() => {
    if (isFullyLoaded) {
      setLoadStrategy('lazy')
    }
  }, [isFullyLoaded])

  const contextValue = useMemo(
    (): DataTableContextValue<TData> => ({
      table,
      columns,
      data,
      totalCount,
      isLoading,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
      fetchAllPages,
      isFullyLoaded,
      loadStrategy,
      sorting,
      setSorting,
      containerRef: containerRef as React.RefObject<HTMLDivElement>,
      estimatedRowHeight,
    }),
    [
      table,
      columns,
      data,
      totalCount,
      isLoading,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
      fetchAllPages,
      isFullyLoaded,
      loadStrategy,
      sorting,
      estimatedRowHeight,
    ],
  )

  return (
    <DataTableContext.Provider
      value={contextValue as DataTableContextValue<unknown>}
    >
      <div
        id={`data-table-${tableId}`}
        className={twMerge(
          'overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900',
          className,
        )}
      >
        {children}
      </div>
    </DataTableContext.Provider>
  )
}

// ============================================================================
// Header Component
// ============================================================================

function DataTableHeader({
  className,
  autoSize,
  lastColumnMin,
  sticky,
}: DataTableHeaderProps) {
  const { table, isFetchingNextPage, loadStrategy } = useDataTableContext()

  const headers = table.getHeaderGroups()[0]?.headers ?? []
  const gridTemplate = autoSize
    ? getAutoGridTemplate(headers.length, lastColumnMin)
    : getGridTemplate(headers)

  // Show loading when eagerly fetching for sort
  const isLoadingForSort = loadStrategy === 'eager' && isFetchingNextPage

  return (
    <div
      className={twMerge(
        'border-b border-primary-100 bg-primary-50/50 dark:border-primary-800 dark:bg-primary-800/50',
        sticky &&
          'sticky top-0 z-10 bg-primary-50 shadow-[0_1px_2px_0_rgba(0,0,0,0.03)] dark:bg-primary-900 dark:shadow-[0_1px_2px_0_rgba(0,0,0,0.15)]',
        className,
      )}
      style={{ minWidth: 'var(--dt-scroll-width, auto)' }}
    >
      {table.getHeaderGroups().map((headerGroup) => (
        <div
          key={headerGroup.id}
          className="grid"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {headerGroup.headers.map((header) => {
            const canSort = header.column.getCanSort()
            const sortDirection = header.column.getIsSorted()

            return (
              <div
                key={header.id}
                className={twMerge(
                  'whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase text-primary-500 dark:text-primary-400',
                  canSort &&
                    'cursor-pointer select-none hover:text-primary-700 dark:hover:text-primary-300',
                )}
                onClick={
                  canSort ? header.column.getToggleSortingHandler() : undefined
                }
              >
                <span className="flex items-center gap-1.5">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                  {canSort && (
                    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
                      {isLoadingForSort && sortDirection ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : sortDirection === 'asc' ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : sortDirection === 'desc' ? (
                        <ArrowDown className="h-3 w-3" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-40" />
                      )}
                    </span>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// Scroll Area Component (unified scroll for header + body)
// ============================================================================

interface ScrollOverflow {
  top: boolean
  bottom: boolean
  left: boolean
  right: boolean
}

function DataTableScrollArea({
  maxHeight,
  children,
  className,
}: DataTableScrollAreaProps) {
  const { containerRef } = useDataTableContext()
  const [overflow, setOverflow] = useState<ScrollOverflow>({
    top: false,
    bottom: false,
    left: false,
    right: false,
  })
  const [canScroll, setCanScroll] = useState({ x: false, y: false })

  // Set CSS variable for scroll content width and track overflow/scroll state
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let rafId: number | null = null

    const updateScrollState = () => {
      // Cancel any pending RAF to avoid stacking updates
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(() => {
        if (!container) return

        const {
          scrollLeft,
          scrollTop,
          scrollWidth,
          scrollHeight,
          clientWidth,
          clientHeight,
        } = container

        // Update CSS variable for width sync
        container.style.setProperty('--dt-scroll-width', `${scrollWidth}px`)

        // Calculate overflow state (with small threshold for rounding)
        const threshold = 2
        const canScrollX = scrollWidth > clientWidth + threshold
        const canScrollY = scrollHeight > clientHeight + threshold
        const newOverflow: ScrollOverflow = {
          top: canScrollY && scrollTop > threshold,
          bottom:
            canScrollY && scrollTop < scrollHeight - clientHeight - threshold,
          left: canScrollX && scrollLeft > threshold,
          right:
            canScrollX && scrollLeft < scrollWidth - clientWidth - threshold,
        }

        // Update overflow state for shadows
        setOverflow(newOverflow)
        setCanScroll({ x: canScrollX, y: canScrollY })

        rafId = null
      })
    }

    // Initial measurement
    updateScrollState()

    // Listen to scroll events
    container.addEventListener('scroll', updateScrollState, { passive: true })

    // Re-measure on resize using ResizeObserver
    const observer = new ResizeObserver(updateScrollState)
    observer.observe(container)

    return () => {
      container.removeEventListener('scroll', updateScrollState)
      observer.disconnect()
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [containerRef])

  return (
    <div className="relative">
      {/* Scroll container */}
      <div
        ref={containerRef}
        className={className}
        style={{
          maxHeight,
          overflowY: 'auto',
          overflowX: canScroll.x ? 'auto' : 'hidden',
        }}
      >
        {children}
      </div>

      {/* Edge shadows - subtle indicators for scrollable content */}
      <div
        className={twMerge(
          'pointer-events-none absolute inset-y-0 left-0 w-3 bg-gradient-to-r from-black/[0.03] to-transparent transition-opacity duration-150 dark:from-black/10',
          overflow.left ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={twMerge(
          'pointer-events-none absolute inset-y-0 right-[8px] w-3 bg-gradient-to-l from-black/[0.03] to-transparent transition-opacity duration-150 dark:from-black/10',
          overflow.right ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={twMerge(
          'pointer-events-none absolute inset-x-0 top-0 h-3 bg-gradient-to-b from-black/[0.03] to-transparent transition-opacity duration-150 dark:from-black/10',
          overflow.top ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={twMerge(
          'pointer-events-none absolute inset-x-0 bottom-0 h-3 bg-gradient-to-t from-black/[0.03] to-transparent transition-opacity duration-150 dark:from-black/10',
          overflow.bottom ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  )
}

// ============================================================================
// Virtual Body Component
// ============================================================================

function DataTableVirtualBody<TData>({
  renderRow,
  maxHeight = 500,
  overscan = 5,
  className,
  useParentScroll = false,
}: DataTableVirtualBodyProps<TData>) {
  const {
    table,
    containerRef,
    estimatedRowHeight,
    isLoading,
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isFullyLoaded,
  } = useDataTableContext<TData>()

  // Local ref for when we create our own scroll container
  const localContainerRef = useRef<HTMLDivElement>(null)
  const scrollRef = useParentScroll ? containerRef : localContainerRef

  const rows = table.getRowModel().rows

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan,
  })

  // Force virtualizer to recalculate after mount when scroll element is available
  // This fixes the "blank until scroll" issue when using parent scroll
  const didInitialMeasure = useRef(false)
  useEffect(() => {
    if (didInitialMeasure.current) return
    // Use requestAnimationFrame to ensure DOM is ready before measuring
    const frame = requestAnimationFrame(() => {
      if (scrollRef.current) {
        virtualizer.measure()
        didInitialMeasure.current = true
      }
    })
    return () => cancelAnimationFrame(frame)
  })

  const virtualRows = virtualizer.getVirtualItems()

  // Handle scroll-based loading - check if we're near the bottom
  const handleScroll = useCallback(() => {
    const container = scrollRef.current
    if (!container || isFetchingNextPage || !hasNextPage || isFullyLoaded)
      return

    const { scrollTop, scrollHeight, clientHeight } = container
    // Trigger load when within 400px of the bottom (load earlier for smoother UX)
    if (scrollHeight - scrollTop - clientHeight < 400) {
      fetchNextPage()
    }
  }, [scrollRef, isFetchingNextPage, hasNextPage, isFullyLoaded, fetchNextPage])

  // Attach scroll listener
  useEffect(() => {
    const container = scrollRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [scrollRef, handleScroll])

  // Empty state
  if (!isLoading && data.length === 0) {
    return null
  }

  const content = (
    <>
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualRows.map((virtualRow) => {
          const row = rows[virtualRow.index]
          return (
            <div
              key={row.id}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {renderRow(row, virtualRow.index)}
            </div>
          )
        })}
      </div>
      {/* Loading indicator at bottom of scroll area */}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-3">
          <Loader2 className="h-4 w-4 animate-spin text-primary-500" />
          <span className="ml-2 text-sm text-primary-500">Loading more...</span>
        </div>
      )}
    </>
  )

  // When using parent scroll, don't wrap in our own scroll container
  // Use CSS variable to ensure rows expand to fill available width
  if (useParentScroll) {
    return (
      <div
        className={className}
        style={{ width: 'var(--dt-scroll-width, 100%)' }}
      >
        {content}
      </div>
    )
  }

  // Default: create our own scroll container
  return (
    <div
      ref={localContainerRef}
      className={twMerge('overflow-y-auto', className)}
      style={{ maxHeight }}
    >
      {content}
    </div>
  )
}

// ============================================================================
// Simple Body Component (non-virtualized, for small datasets)
// ============================================================================

interface DataTableBodyProps<TData> {
  /** Render function for each row */
  renderRow: (row: Row<TData>, index: number) => React.ReactNode
  /** Maximum height for scrollable area */
  maxHeight?: number | string
  /** Additional className */
  className?: string
}

function DataTableBody<TData>({
  renderRow,
  maxHeight,
  className,
}: DataTableBodyProps<TData>) {
  const { table, isLoading, data } = useDataTableContext<TData>()
  const rows = table.getRowModel().rows

  if (!isLoading && data.length === 0) {
    return null
  }

  return (
    <div className={twMerge('overflow-auto', className)} style={{ maxHeight }}>
      {rows.map((row, index) => renderRow(row, index))}
    </div>
  )
}

// ============================================================================
// Row Component (for use in renderRow)
// ============================================================================

interface DataTableRowProps {
  /** Row data from TanStack Table */
  row: Row<unknown>
  /** Additional className */
  className?: string
}

const DataTableRow = React.memo(function DataTableRow({
  row,
  className,
}: DataTableRowProps) {
  const cells = row.getVisibleCells()
  const gridTemplate = getGridTemplate(cells.map((c) => c.column))

  return (
    <div
      className={twMerge(
        'grid border-b border-primary-100 transition-colors hover:bg-primary-50 dark:border-primary-800 dark:hover:bg-primary-800',
        className,
      )}
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {cells.map((cell) => (
        <div
          key={cell.id}
          className="min-w-0 px-4 py-3 text-sm text-primary-700 dark:text-primary-300"
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      ))}
    </div>
  )
})

// ============================================================================
// Load Trigger Component (Intersection Observer)
// ============================================================================

function DataTableLoadTrigger({ className }: DataTableLoadTriggerProps) {
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isFullyLoaded } =
    useDataTableContext()

  const handleIntersect = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const sentinelRef = useIntersectionObserver(handleIntersect, {
    enabled: hasNextPage && !isFetchingNextPage && !isFullyLoaded,
    rootMargin: '200px',
  })

  if (!hasNextPage && !isFetchingNextPage) {
    return null
  }

  return (
    <div
      ref={sentinelRef}
      className={twMerge('flex items-center justify-center py-4', className)}
    >
      {isFetchingNextPage && (
        <div className="flex items-center gap-2 text-sm text-primary-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading more...</span>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Empty Component
// ============================================================================

function DataTableEmpty({
  children = 'No data available.',
  className,
}: DataTableEmptyProps) {
  const { data, isLoading } = useDataTableContext()

  if (isLoading || data.length > 0) {
    return null
  }

  return (
    <div
      className={twMerge(
        'py-12 text-center text-sm text-primary-500 dark:text-primary-400',
        className,
      )}
    >
      {children}
    </div>
  )
}

// ============================================================================
// Skeleton Component
// ============================================================================

function DataTableSkeleton({
  rowCount = 5,
  columnCount = 4,
  className,
}: DataTableSkeletonProps) {
  return (
    <div
      className={twMerge(
        'overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900',
        className,
      )}
    >
      {/* Header skeleton */}
      <div className="flex border-b border-primary-100 bg-primary-50/50 dark:border-primary-800 dark:bg-primary-800/50">
        {Array.from({ length: columnCount }).map((_, i) => (
          <div key={i} className="flex-1 px-4 py-3">
            <div className="h-3 w-16 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
          </div>
        ))}
      </div>
      {/* Body skeleton */}
      <div>
        {Array.from({ length: rowCount }).map((_, rowIdx) => (
          <div
            key={rowIdx}
            className="flex border-b border-primary-100 dark:border-primary-800"
          >
            {Array.from({ length: columnCount }).map((_, colIdx) => (
              <div key={colIdx} className="flex-1 px-4 py-3">
                <div
                  className="h-4 animate-pulse rounded bg-primary-200/60 dark:bg-primary-700/60"
                  style={{ width: `${60 + Math.random() * 30}%` }}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// Compound Component Export
// ============================================================================

/**
 * DataTableV2 - Compositional data table with virtualization and sorting
 *
 * Features:
 * - TanStack Table for sorting/filtering
 * - TanStack Virtual for virtualized rows (large datasets)
 * - Intersection Observer for auto-load on scroll
 * - Hybrid strategy: lazy load by default, eager load for sort/filter
 *
 * Design Notes:
 * - Uses compound component pattern with context for shared state
 * - Generic type flows through context but consumers may need type assertions
 *   for custom row renderers (this is a tradeoff of the compound component pattern)
 * - For type-safe row rendering, cast the row type in renderRow callback
 *
 * @example
 * ```tsx
 * <DataTableV2
 *   data={items}
 *   totalCount={total}
 *   columns={columns}
 *   hasNextPage={hasNextPage}
 *   fetchNextPage={fetchNextPage}
 *   isFullyLoaded={!hasNextPage}
 * >
 *   <DataTableV2.Header />
 *   <DataTableV2.VirtualBody
 *     renderRow={(row) => <MyRow row={row as Row<MyType>} />}
 *     maxHeight={500}
 *   />
 *   <DataTableV2.LoadTrigger />
 *   <DataTableV2.Empty>No data found.</DataTableV2.Empty>
 * </DataTableV2>
 * ```
 */
export const DataTableV2 = DataTableRoot as typeof DataTableRoot & {
  Root: typeof DataTableRoot
  Header: typeof DataTableHeader
  ScrollArea: typeof DataTableScrollArea
  VirtualBody: typeof DataTableVirtualBody
  Body: typeof DataTableBody
  Row: typeof DataTableRow
  LoadTrigger: typeof DataTableLoadTrigger
  Empty: typeof DataTableEmpty
  Skeleton: typeof DataTableSkeleton
}

// Add Root alias for explicit compound component usage
DataTableV2.Root = DataTableRoot
DataTableV2.Header = DataTableHeader
DataTableV2.ScrollArea = DataTableScrollArea
DataTableV2.VirtualBody = DataTableVirtualBody
DataTableV2.Body = DataTableBody
DataTableV2.Row = DataTableRow
DataTableV2.LoadTrigger = DataTableLoadTrigger
DataTableV2.Empty = DataTableEmpty
DataTableV2.Skeleton = DataTableSkeleton

// Re-export context hook and utilities for advanced usage
export { useDataTableContext } from './context'
export { getGridTemplate, getAutoGridTemplate } from './hooks'
export type { DataTableContextValue, LoadStrategy } from './context'
